/**
 * Per-member CAPABILITY MATRIX (chantier C, 2026-09-25).
 *
 * The owner grants, member by member and band by band, a set of fine-grained
 * capabilities instead of the two ad-hoc booleans (`finance_access`,
 * `layout_access`) that used to back the Finance tab and the PDF layout
 * editors. The matrix is the SINGLE source of truth server-side
 * (`user_band_permissions` table, `hasPermission` / `can` in server/authz.js)
 * and this module is its single counterpart in the SPA: the sidebar, the page
 * guards, the editors and the Access panel all read `can()` / `canAny*()`, so
 * the UI can never drift from the server rule.
 *
 * NON-REGRESSION: the API still returns the two legacy booleans as DERIVED
 * values (compat, other call sites) — `can()` falls back to them when the
 * `permissions` array is absent from a payload, which is exactly the state of
 * every existing account (`finance_access = 0`, `layout_access = 0` in
 * production). Nobody gains or loses anything by default.
 *
 * Eleven delegable capabilities, names FROZEN with the backend lot: the 7
 * historical (`layout.*`, `finance.*`) plus the 4 `content.*` names of the
 * « delegated content » lot (2026-09-25). Authority
 * capabilities (il n'y a pas de role en base) are NEVER delegable and must
 * never appear in the UI: member management, invitations, API keys, platform
 * admin. A member must never be able to grant itself a right.
 */

/** The 11 delegable capability names — exact, frozen, ordered as displayed. */
export const PERMISSIONS = [
  'layout.rider',
  'layout.daysheet',
  'layout.tourbook',
  'layout.presets',
  // ⚠️ The 7 historical names keep their POSITIONS (the first 7 of this array are
  // pinned by a test): every new name is appended INSIDE its family block, after
  // the frozen ones — never before.
  'finance.expenses',
  'finance.fees',
  'finance.budget',
  // The « voir » level of the finance family (2026-10-08): opens the money for
  // READING without any write. Real on this family because the finance READS are
  // actually gated server-side (`canAccessFinance` / `canAccessBudget` /
  // `canReadFinanceShow`), unlike the content pages, whose reads are open to every
  // member. Mirrors `server/lib/permissions.js` name for name.
  'finance.view',
  'content.setlists',
  'content.songbook',
  'content.checklists',
  'content.rider',
  // The PEOPLE registry (2026-10-08): see the roster + create/edit a card's
  // STRUCTURAL fields. Never a colleague's personal data (identity, contacts,
  // health), never documents/scans, never deletion — those stay owner/self.
  'team.edit',
  // Droits par SECTION du config rider (2026-10-08) — APPENDUS EN QUEUE : l'ordre
  // canonique des noms historiques (les 7 premiers) et de `team.edit` reste figé.
  'content.rider.general',
  'content.rider.hospitality',
  'content.rider.sound',
  'content.rider.iem',
  'content.rider.specs',
  'content.rider.backline',
  'content.rider.stageDesign',
  'content.rider.lights',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/**
 * Pages HORS contenu (2026-10-13, lot 3a) dont la LECTURE est ouverte à tout
 * membre, SANS capacité d'écriture délégable correspondante (leurs écritures
 * obéissent à d'autres règles : catalogue partagé, `team.edit`, rôle editor sur
 * la disponibilité). Le niveau « — » ne peut donc s'exprimer que par un refus de
 * lecture explicite `<page>.view` — même mécanique que les familles `content.*`,
 * sans la capacité. Ce ne sont **jamais** des droits : elles n'entrent PAS dans
 * `PERMISSIONS`, ne sont jamais accordables, et `normalizePermissions` les ignore.
 * Noms FIGÉS, miroir exact de `DENIABLE_PAGE_FAMILIES` (server/lib/permissions.js).
 */
export const DENIABLE_PAGE_FAMILIES = [
  'venues', 'contacts', 'team', 'availability', 'aiAssistant',
  // Family TOUR (2026-10-14, lot 3b — option B) : Itinerary / Tours / Advancing
  // deviennent déniables. `shows` reste HORS périmètre (l'accueil EST la liste des
  // dates) et n'a donc PAS de marqueur `<page>.view`.
  'itinerary', 'tours', 'advancing',
] as const;
export type DeniablePage = (typeof DENIABLE_PAGE_FAMILIES)[number];

/**
 * Ce que la grille page×niveau sait adresser : une capacité délégable OU une page
 * déniable sans capacité. Les deux partagent `PERMISSION_LEVELS`/labels/pages ;
 * seule une capacité est accordable (✏️), une page déniable n'offre que 👁 / —.
 */
export type AccessFamily = Permission | DeniablePage;

export const LAYOUT_PERMISSIONS: readonly Permission[] = [
  'layout.rider',
  'layout.daysheet',
  'layout.tourbook',
  'layout.presets',
];

export const FINANCE_PERMISSIONS: readonly Permission[] = [
  'finance.expenses',
  'finance.fees',
  'finance.budget',
];

/**
 * Everything that OPENS the finance surface for READING: the « voir » level plus
 * the three write families (which imply reading). Use this for VISIBILITY gates
 * (`canAnyFinance`); keep `FINANCE_PERMISSIONS` for the write families.
 */
export const FINANCE_READ_PERMISSIONS: readonly Permission[] = [
  ...FINANCE_PERMISSIONS,
  'finance.view',
];

export const CONTENT_PERMISSIONS: readonly Permission[] = [
  'content.setlists',
  'content.songbook',
  'content.checklists',
  'content.rider',
];

/**
 * Authority capabilities: owner / superadmin ONLY. Listed here so the UI can
 * prove it never offers them (a test asserts they are absent from every group,
 * preset and label map).
 */
export const NON_DELEGABLE_PERMISSIONS = [
  'band.settings',
  'admin',
  'invites',
  'api_keys',
] as const;

/**
 * The people-registry family (2026-10-08) — delegable like the others, but it is
 * a single capability: it holds no PDF layout, no finance and no rider content.
 */
/**
 * DROITS PAR SECTION du config rider (2026-10-08) — miroir EXACT de
 * `RIDER_SECTION_PERMISSIONS` (server/lib/permissions.js). Le grain est la PAGE de
 * config : un éclairagiste tient `content.rider.lights` et le serveur PRÉSERVE les
 * autres sections (`enforceForbidden`), puisque l'autosave envoie le blob entier.
 * Ajouter une section ici SANS l'ajouter au serveur (ou l'inverse) ferait une case
 * qui n'écrit rien — un test compare les deux listes.
 */
export const CONTENT_SECTION_PERMISSIONS: readonly Permission[] = [
  'content.rider.general',
  'content.rider.hospitality',
  'content.rider.sound',
  'content.rider.iem',
  'content.rider.specs',
  'content.rider.backline',
  'content.rider.stageDesign',
  'content.rider.lights',
];

export const TEAM_PERMISSIONS: readonly Permission[] = ['team.edit'];

export function isPermission(name: unknown): name is Permission {
  return typeof name === 'string' && (PERMISSIONS as readonly string[]).includes(name);
}

/** Le nom est-il une PAGE déniable (sans capacité) — `venues`, `team`, … ? */
export function isDeniablePageFamily(name: unknown): name is DeniablePage {
  return typeof name === 'string' && (DENIABLE_PAGE_FAMILIES as readonly string[]).includes(name);
}

export function isLayoutPermission(name: unknown): boolean {
  return typeof name === 'string' && name.startsWith('layout.');
}

export function isFinancePermission(name: unknown): boolean {
  return typeof name === 'string' && name.startsWith('finance.');
}

/**
 * Le nom désigne-t-il un DROIT PAR SECTION du config rider (`content.rider.lights`) ?
 * Ces noms ne sont ni des layout, ni des finance, ni des content : sans ce prédicat,
 * la vérification « chaque capacité appartient à une famille » les déclarerait orphelines.
 */
export function isRiderSectionPermission(name: unknown): boolean {
  return (CONTENT_SECTION_PERMISSIONS as readonly unknown[]).includes(name);
}

export function isTeamPermission(name: unknown): boolean {
  return typeof name === 'string' && name.startsWith('team.');
}

export function isContentPermission(name: unknown): boolean {
  return typeof name === 'string' && name.startsWith('content.');
}

/**
 * Normalise a server payload into a clean, canonical permission set: unknown
 * names dropped (a non-delegable name from a hand-edited payload is IGNORED,
 * never rendered as a checkbox), duplicates removed, canonical order enforced
 * so comparisons and tests are stable.
 */
export function normalizePermissions(raw: unknown): Permission[] {
  if (!Array.isArray(raw)) return [];
  const kept = new Set<Permission>();
  for (const value of raw) {
    if (isPermission(value)) kept.add(value);
  }
  return PERMISSIONS.filter((p) => kept.has(p));
}

/**
 * Should the « Rider config » sidebar section be VISIBLE?
 *
 * The section holds the 8 technical config pages + the stage-plot editor. It is a
 * CAPABILITY question, not a role question: the server exempts a holder of
 * `content.rider` from the global read-only guard for `PUT /api/bands/:id/rider`
 * (see `hasPermission` in server/authz.js). A `tech` (ex-`crew`) member holds that
 * capability BY ROLE — gating this section on the role alone hid those pages from
 * the very people allowed to edit them (real case, 2026-10-08: Seb saw only the
 * preview). Mirrors the server rule: role >= editor OR the capability.
 *
 * NOTE: this decides VISIBILITY only. The write right itself stays enforced by
 * `writeGate` on the page and by the server on the route — hiding a link has never
 * been a protection (see the `Role` note in bandz-frontend/rbac-and-dual-role).
 */
export interface RiderConfigAccessInput {
  isSuperAdmin: boolean;
  isShowcase: boolean;
  bandRole?: string | null;
  permissions?: readonly string[] | null;
}

export function canSeeRiderConfig(input: RiderConfigAccessInput): boolean {
  if (input.isShowcase) return true;                 // the shared demo tours the product
  if (input.isSuperAdmin) return true;               // implicit owner of every band
  if (input.bandRole === 'owner' || input.bandRole === 'editor') return true;
  const grants = input.permissions ? [...input.permissions] : undefined;
  if (can({ permissions: grants }, 'content.rider')) return true;
  // Un titulaire d'une SECTION doit VOIR la section de config : sinon il ne pourrait
  // pas atteindre la page qu'il a le droit d'écrire (même règle que le serveur,
  // `canWriteRiderConfig`). Ce qu'il écrit est borné par le planificateur.
  return CONTENT_SECTION_PERMISSIONS.some((p) => can({ permissions: grants }, p));
}

/** i18n label of each capability (a checkbox in the Access panel). */
export const PERMISSION_LABEL_KEY: Record<AccessFamily, string> = {
  'layout.rider': 'access.permLayoutRider',
  'layout.daysheet': 'access.permLayoutDaysheet',
  'layout.tourbook': 'access.permLayoutTourbook',
  'layout.presets': 'access.permLayoutPresets',
  'finance.view': 'access.permFinanceView',
  'finance.expenses': 'access.permFinanceExpenses',
  'finance.fees': 'access.permFinanceFees',
  'finance.budget': 'access.permFinanceBudget',
  'content.setlists': 'access.permContentSetlists',
  'content.songbook': 'access.permContentSongbook',
  'content.checklists': 'access.permContentChecklists',
  'content.rider': 'access.permContentRider',
  'team.edit': 'access.permTeamEdit',
  'content.rider.general': 'access.permRiderGeneral',
  'content.rider.hospitality': 'access.permRiderHospitality',
  'content.rider.sound': 'access.permRiderSound',
  'content.rider.iem': 'access.permRiderIem',
  'content.rider.specs': 'access.permRiderSpecs',
  'content.rider.backline': 'access.permRiderBackline',
  'content.rider.stageDesign': 'access.permRiderStageDesign',
  'content.rider.lights': 'access.permRiderLights',
  // Pages hors contenu (2026-10-13, lot 3a) : lecture ouverte à tout membre, refus
  // explicite possible. Le libellé dit « read access » (aucune écriture délégable).
  venues: 'access.permPageVenues',
  contacts: 'access.permPageContacts',
  team: 'access.permPageTeam',
  availability: 'access.permPageAvailability',
  aiAssistant: 'access.permPageAiAssistant',
  // Family Tour (2026-10-14, lot 3b) : lecture ouverte, refus explicite possible.
  itinerary: 'access.permPageItinerary',
  tours: 'access.permPageTours',
  advancing: 'access.permPageAdvancing',
};

/**
 * "Where it applies" sub-label of each capability — the page grouping requested
 * by the brief, shown under the checkbox so the owner knows what opening the
 * right actually unlocks.
 */
export const PERMISSION_PAGE_KEY: Record<AccessFamily, string> = {
  'layout.rider': 'access.pageRiderLayout',
  'layout.daysheet': 'access.pageDaysheetLayout',
  'layout.tourbook': 'access.pageTourbookLayout',
  'layout.presets': 'access.pageLayoutPresets',
  'finance.view': 'access.pageFinanceView',
  'finance.expenses': 'access.pageShowExpenses',
  'finance.fees': 'access.pageShowFees',
  'finance.budget': 'access.pageTourBudget',
  'content.setlists': 'access.pageSetlists',
  'content.songbook': 'access.pageSongbook',
  'content.checklists': 'access.pageChecklists',
  'content.rider': 'access.pageRiderContent',
  'team.edit': 'access.pageTeamRegistry',
  // Une section écrit UNE page de config : la colonne « s'applique à » réutilise la
  // clé de la page (déjà ×3 langues) — aucune copie de libellé.
  'content.rider.general': 'access.pageConfigGeneral',
  'content.rider.hospitality': 'access.pageConfigHospitality',
  'content.rider.sound': 'access.pageConfigSound',
  'content.rider.iem': 'access.pageConfigIem',
  'content.rider.specs': 'access.pageConfigAudioSpecs',
  'content.rider.backline': 'access.pageConfigBackline',
  'content.rider.stageDesign': 'access.pageConfigStageDesign',
  'content.rider.lights': 'access.pageConfigLights',
  venues: 'access.pageOfVenues',
  contacts: 'access.pageOfContacts',
  team: 'access.pageOfTeam',
  availability: 'access.pageOfAvailability',
  aiAssistant: 'access.pageOfAiAssistant',
  itinerary: 'access.pageOfItinerary',
  tours: 'access.pageOfTours',
  advancing: 'access.pageOfAdvancing',
};

export interface PermissionGroup {
  id: string;
  /** i18n key of the group heading. */
  labelKey: string;
  /** Une capacité accordable OU une page déniable (👁 / — seulement). */
  permissions: readonly AccessFamily[];
}

/** The capabilities grouped BY PAGE, as rendered in the panel. */
export const PERMISSION_GROUPS: readonly PermissionGroup[] = [
  { id: 'layout', labelKey: 'access.groupLayout', permissions: LAYOUT_PERMISSIONS },
  { id: 'finance', labelKey: 'access.groupFinance', permissions: FINANCE_READ_PERMISSIONS },
  { id: 'content', labelKey: 'access.groupContent', permissions: CONTENT_PERMISSIONS },
  // The people registry (2026-10-08) — kept LAST so the existing groups keep
  // their display order.
  { id: 'team', labelKey: 'access.groupTeam', permissions: ['team.edit'] },
  // Les SECTIONS du config rider (2026-10-08) — dernier groupe : un éclairagiste
  // s'accorde ici, sans ouvrir tout le config.
  { id: 'rider-sections', labelKey: 'access.groupRiderSections', permissions: CONTENT_SECTION_PERMISSIONS },
  // Les PAGES hors contenu (2026-10-13, lot 3a) — ajouté EN QUEUE : leur seule
  // action est le niveau « — » (refus de lecture), jamais un octroi d'écriture.
  { id: 'pages', labelKey: 'access.groupPages', permissions: DENIABLE_PAGE_FAMILIES },
];

export interface PermissionPreset {
  id: string;
  /** i18n key of the preset button. */
  labelKey: string;
  /** The COMPLETE set granted by the preset (it replaces the current set). */
  permissions: readonly Permission[];
}

/**
 * The 4 presets requested by the brief. « Editor » is the plain per-band editor
 * role with no extra capability (it CLEARS the matrix); « Editor + layouts »
 * grants the 4 `layout.*`; « Finance only » grants the 3 `finance.*`; « Crew »
 * grants the 4 `layout.*` PLUS the 4 `content.*` — the real crew case (a member
 * who runs the band's documents AND its editorial content).
 */
export const PERMISSION_PRESETS: readonly PermissionPreset[] = [
  { id: 'editor', labelKey: 'access.presetEditor', permissions: [] },
  { id: 'editor-layouts', labelKey: 'access.presetEditorLayouts', permissions: LAYOUT_PERMISSIONS },
  { id: 'finance-only', labelKey: 'access.presetFinanceOnly', permissions: FINANCE_PERMISSIONS },
  { id: 'tech', labelKey: 'access.presetTech', permissions: [...LAYOUT_PERMISSIONS, ...CONTENT_PERMISSIONS] },
  // Added 2026-10-08 (user request: « corrige les presets ld/musicien »).
  //  * `musician` = NO capability, and that is the HONEST preset: the content pages
  //    are readable by every member, so a musician reads the setlists and the
  //    songbook, and edits HIS OWN technical card through `/api/me/rider-card` with
  //    no capability at all. The preset CLEARS the matrix (it pairs with the Viewer
  //    role): it never pretends to grant a right that does not exist.
  { id: 'musician', labelKey: 'access.presetMusician', permissions: [] },
  //  * `accountant-read` = read the money, never touch it (finance.view only).
  { id: 'accountant-read', labelKey: 'access.presetAccountantRead', permissions: ['finance.view'] },
  //  * `ld` (light designer) = rider content + the rider PDF layout. The label is
  //    deliberately NOT « lights only »: the band config is ONE blob written by ONE
  //    route, so a section-level right (« lights ») is NOT enforceable today. The
  //    preset opens the whole config and the label says so.
  // ⚠️ LD est devenu un VRAI droit « éclairage seulement » (2026-10-08) : il tenait
  // `content.rider` (tout le config) parce qu'un droit par section n'existait pas.
  { id: 'ld', labelKey: 'access.presetLd', permissions: ['content.rider.lights', 'layout.rider'] },
];

/* ------------------------------------------------------------------ */
/*  Levels — the page×level grid (2026-10-08)                          */
/* ------------------------------------------------------------------ */

/** The three states a family can be in for a given member. */
export type AccessLevel = 'none' | 'view' | 'edit';

/**
 * How a family reacts to the three levels. The app does NOT have the same read
 * rule everywhere, and the grid must say what it really does instead of drawing
 * three identical buttons:
 *
 *   - `openReads`: every member of the band already reads this family (the content
 *     pages). « 👁 view » therefore needs NO grant, and « — » is expressed by an
 *     EXPLICIT DENY (`denyName`) — otherwise absence of a grant would keep meaning
 *     « reads allowed » and every existing member would have to be rewritten.
 *   - `viewName`: the read level IS a capability (`finance.view`); « 👁 » grants it.
 *   - neither: the family has no read-only level at all (the layout editors, the
 *     people registry): « 👁 » is unavailable and the grid DISABLES it with a
 *     reason, rather than offering a button that does nothing.
 */
export interface LevelSpec {
  viewName: Permission | null;
  openReads: boolean;
  denyName: string | null;
  /**
   * Une capacité accordable offre le niveau ✏️. Une PAGE déniable (`venues`, …)
   * n'a AUCUNE écriture délégable : `grantable: false` désactive ✏️ (avec sa
   * raison) au lieu d'offrir un bouton qui accorderait un droit inexistant.
   */
  grantable?: boolean;
}

/** The deny markers the server accepts (see server/lib/permissions.js). */
export const DENIABLE_VIEW_PERMISSIONS: readonly string[] = [
  'content.setlists.view', 'content.songbook.view', 'content.checklists.view', 'content.rider.view',
  // Pages hors contenu (2026-10-13, lot 3a) — miroir de `DENIABLE_VIEW_NAMES`.
  'venues.view', 'contacts.view', 'team.view', 'availability.view', 'aiAssistant.view',
  // Family Tour (2026-10-14, lot 3b) — miroir serveur. `shows.view` n'existe PAS.
  'itinerary.view', 'tours.view', 'advancing.view',
];

export const PERMISSION_LEVELS: Record<AccessFamily, LevelSpec> = {
  'layout.rider': { viewName: null, openReads: false, denyName: null },
  'layout.daysheet': { viewName: null, openReads: false, denyName: null },
  'layout.tourbook': { viewName: null, openReads: false, denyName: null },
  'layout.presets': { viewName: null, openReads: false, denyName: null },
  'finance.expenses': { viewName: 'finance.view', openReads: false, denyName: null },
  'finance.fees': { viewName: 'finance.view', openReads: false, denyName: null },
  'finance.budget': { viewName: 'finance.view', openReads: false, denyName: null },
  'finance.view': { viewName: null, openReads: false, denyName: null },
  'content.rider': { viewName: null, openReads: true, denyName: 'content.rider.view' },
  'content.setlists': { viewName: null, openReads: true, denyName: 'content.setlists.view' },
  'content.songbook': { viewName: null, openReads: true, denyName: 'content.songbook.view' },
  'content.checklists': { viewName: null, openReads: true, denyName: 'content.checklists.view' },
  'team.edit': { viewName: null, openReads: false, denyName: null },
  // Une SECTION n'a pas de niveau « voir » séparé : elle ouvre la page de config en
  // ÉCRITURE (le reste du config est préservé). Le 👁 est donc désactivé AVEC sa raison.
  'content.rider.general': { viewName: null, openReads: false, denyName: null },
  'content.rider.hospitality': { viewName: null, openReads: false, denyName: null },
  'content.rider.sound': { viewName: null, openReads: false, denyName: null },
  'content.rider.iem': { viewName: null, openReads: false, denyName: null },
  'content.rider.specs': { viewName: null, openReads: false, denyName: null },
  'content.rider.backline': { viewName: null, openReads: false, denyName: null },
  'content.rider.stageDesign': { viewName: null, openReads: false, denyName: null },
  'content.rider.lights': { viewName: null, openReads: false, denyName: null },
  // Pages hors contenu (2026-10-13, lot 3a) : lecture ouverte, refus explicite
  // possible, AUCUNE écriture délégable (`grantable: false` ⇒ ✏️ désactivé).
  venues: { viewName: null, openReads: true, denyName: 'venues.view', grantable: false },
  contacts: { viewName: null, openReads: true, denyName: 'contacts.view', grantable: false },
  team: { viewName: null, openReads: true, denyName: 'team.view', grantable: false },
  availability: { viewName: null, openReads: true, denyName: 'availability.view', grantable: false },
  aiAssistant: { viewName: null, openReads: true, denyName: 'aiAssistant.view', grantable: false },
  // Family Tour (2026-10-14, lot 3b) : lecture ouverte à tout membre, refus
  // explicite possible, aucune écriture délégable. `shows` n'y figure PAS.
  itinerary: { viewName: null, openReads: true, denyName: 'itinerary.view', grantable: false },
  tours: { viewName: null, openReads: true, denyName: 'tours.view', grantable: false },
  advancing: { viewName: null, openReads: true, denyName: 'advancing.view', grantable: false },
};

/** Anything carrying a deny list (the API payloads expose it). */
export interface WithDenied {
  denied?: string[] | null;
}

/** True when the member is EXPLICITLY denied reading this family. */
export function isFamilyDenied(source: WithDenied | null | undefined, family: AccessFamily): boolean {
  const deny = PERMISSION_LEVELS[family]?.denyName;
  if (!deny || !source) return false;
  return Array.isArray(source.denied) && source.denied.includes(deny);
}

/** The current level of a family for a member, derived from grants + denies. */
export function levelOf(
  source: (PermissionSource & WithDenied) | null | undefined,
  family: AccessFamily,
): AccessLevel {
  if (!source) return 'none';
  if (isFamilyDenied(source, family)) return 'none';
  const granted = memberPermissions(source);
  // Une PAGE déniable n'est jamais un grant : seules les capacités s'accordent.
  if (isPermission(family) && granted.includes(family)) return 'edit';
  const spec = PERMISSION_LEVELS[family];
  if (spec?.openReads) return 'view';                        // reads are open to members
  if (spec?.viewName && granted.includes(spec.viewName)) return 'view';
  return 'none';
}

/**
 * The COMPLETE state a family must reach for a member: `{ permissions, denied }`.
 * Pure — the caller persists BOTH sets. Setting a level never grants a write: the
 * « view » level only ever touches `viewName` / `denyName`.
 */
export function applyLevel(
  source: (PermissionSource & WithDenied) | null | undefined,
  family: AccessFamily,
  level: AccessLevel,
): { permissions: Permission[]; denied: string[] } {
  const spec = PERMISSION_LEVELS[family];
  const perms = new Set(memberPermissions(source));
  const denies = new Set(
    (Array.isArray(source?.denied) ? (source as WithDenied).denied! : [])
      .filter((d) => DENIABLE_VIEW_PERMISSIONS.includes(d)),
  );
  // Every level first clears this family's own grant and its deny marker…
  if (isPermission(family)) perms.delete(family);
  if (spec?.viewName) perms.delete(spec.viewName);
  if (spec?.denyName) denies.delete(spec.denyName);
  // …then the requested level writes exactly one of them. Une PAGE déniable n'a
  // pas de niveau ✏️ : demander « edit » sur elle n'accorde RIEN (le bouton est
  // désactivé ; ceci est la ceinture de sécurité).
  if (level === 'edit') {
    if (isPermission(family)) perms.add(family);
  } else if (level === 'view') {
    if (spec?.viewName) perms.add(spec.viewName);
    else if (!spec?.openReads) {
      // No read-only level exists for this family: refuse to pretend. The caller
      // disables that button; this is the belt-and-braces.
      return { permissions: PERMISSIONS.filter((p) => perms.has(p)), denied: [...denies].sort() };
    }
  } else if (level === 'none' && spec?.denyName) {
    denies.add(spec.denyName);
  }
  return { permissions: PERMISSIONS.filter((p) => perms.has(p)), denied: [...denies].sort() };
}

/* ------------------------------------------------------------------ */
/*  Effective decision                                                 */
/* ------------------------------------------------------------------ */

/**
 * Anything carrying a capability matrix: the caller's band (from `GET
 * /api/bands`, `permissions` = the EFFECTIVE set for the signed-in user) or a
 * member row (from `GET /api/bands/:id/members`). The 2 legacy flags are kept
 * as a compatibility fallback for payloads produced before the matrix shipped.
 */
export interface PermissionSource {
  permissions?: string[] | null;
  finance_access?: boolean | number | null;
  layout_access?: boolean | number | null;
}

/** The matrix of a payload, or `null` when the payload predates it. */
function matrixOf(source: PermissionSource | null | undefined): Permission[] | null {
  if (!source) return null;
  return Array.isArray(source.permissions) ? normalizePermissions(source.permissions) : null;
}

/**
 * THE client rule (mirrors `can(req, bandId, permission)` server-side):
 * an explicit matrix decides; without one, the legacy derived flags apply.
 * `band_role === 'owner'` / superadmin are already folded into the effective
 * matrix the API returns, and the legacy flags were derived the same way.
 */
export function can(source: PermissionSource | null | undefined, permission: Permission): boolean {
  if (!source) return false;
  const matrix = matrixOf(source);
  if (matrix) return matrix.includes(permission);
  if (isLayoutPermission(permission)) return source.layout_access === 1 || source.layout_access === true;
  if (isFinancePermission(permission)) return !!source.finance_access;
  return false;
}

/** Any `layout.*` capability (drives the hidden/shown sidebar section). */
export function canAnyLayout(source: PermissionSource | null | undefined): boolean {
  return LAYOUT_PERMISSIONS.some((p) => can(source, p));
}

/** Any `finance.*` capability (drives the hidden/shown Payouts link). */
export function canAnyFinance(source: PermissionSource | null | undefined): boolean {
  // VISIBILITY: a `finance.view` holder opens the finance surface (read-only).
  return FINANCE_READ_PERMISSIONS.some((p) => can(source, p));
}

/* ------------------------------------------------------------------ */
/*  Derived role label (no role is ever stored for this)                */
/* ------------------------------------------------------------------ */

export interface MemberRoleInput extends PermissionSource, WithDenied {
  role?: string | null;
}

/**
 * A member granted at least one `layout.*` capability runs the PDF documents
 * the band hands out. The label is DERIVED at render time and DESCRIBES the
 * granted right only — it never suggests a manager role: nothing is written to
 * the database, `user_bands.role` stays `editor` (a CHECK constraint SQLite
 * cannot alter without rebuilding the table, the riskiest operation in this
 * project).
 */
export function isEditorWithLayouts(member: MemberRoleInput | null | undefined): boolean {
  if (!member) return false;
  if (member.role !== 'editor') return false;
  const matrix = matrixOf(member);
  if (matrix) return matrix.some(isLayoutPermission);
  // Legacy payload: the old `layout_access` flag granted all 3 layout editors.
  return member.layout_access === 1 || member.layout_access === true;
}

/**
 * i18n key of the role shown in the member list: the derived « Editor +
 * layouts » label when the member holds a layout capability, `null` otherwise
 * (the caller falls back to the plain role label).
 */
export function derivedRoleLabelKey(member: MemberRoleInput | null | undefined): string | null {
  return isEditorWithLayouts(member) ? 'access.roleEditorLayouts' : null;
}

/** The effective set of a member row (canonical order, unknowns dropped). */
export function memberPermissions(member: MemberRoleInput | null | undefined): Permission[] {
  return normalizePermissions(member?.permissions);
}